import { computed, inject, Injectable } from '@angular/core';
import { LoginRequest } from '@api/models';
import {
  catchError,
  finalize,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { rxResource } from '@angular/core/rxjs-interop';
import { AuthResponse } from '@core/models/auth.model';
import { UserDetailResponse } from '../models/response/user-detail-response.model';
import { SessionUtils } from '@core/utils/session-utils';
import { JwtUtils } from '@core/utils/jwt-utils';
import { TokenManager } from '@core/utils/token-manager';
import { AuthStateManager } from './auth-state-manager-service';
import { AuthApiService } from './auth-api-service';
import { Employment } from '@core/models/common.model';
import { FoodVenueService } from '../../food-venue/services/food-venue.service';
import { MenuService } from '../../client/services/menu-service';
import { CartService } from '../../client/services/cart-service';
import { TableAccessRequest } from './qr-processing-service';

import { logger } from '@core/utils/logger';
@Injectable({ providedIn: 'root' })
export class AuthService {
  private authApi = inject(AuthApiService);
  private authState = inject(AuthStateManager);
  private foodVenueService = inject(FoodVenueService);
  private menuService = inject(MenuService);
  private cartService = inject(CartService);

  /** Refresh en curso: las peticiones que llegan mientras tanto comparten su resultado (o su error) */
  private refreshInFlight$: Observable<string> | null = null;
  private pendingTableId: string | null = null;

  constructor() {
    this.authState.loadFromStorage();
  }

  checkStatusResource = rxResource({
    stream: () => this.checkAuthStatus(),
  });

  authStatus = this.authState.authStatus;
  accessToken = this.authState.accessToken;
  refreshToken = this.authState.refreshToken;
  expirationDate = this.authState.expirationDate;
  tableSessionId = this.authState.tableSessionId;
  foodVenueId = this.authState.foodVenueId;
  isAuthenticated = this.authState.isAuthenticated;
  isGuest = this.authState.isGuest;
  participantId = this.authState.participantId;
  employments = this.authState.employments;
  role = this.authState.role;

  authStatusText = computed(() => {
    const status = this.authState.authStatus();
    if (status === 'checking') return 'Verificando...';
    if (status === 'authenticated') return 'Autenticado';
    return 'No autenticado';
  });

  public currentUser = computed(() => {
    const token = this.accessToken();
    if (!token) return null;

    try {
      const name =
        JwtUtils.getClaimValue(token, 'name') || JwtUtils.getClaimValue(token, 'sub') || 'Usuario';

      return {
        name: name,
        role: this.authState.role(),
      };
    } catch {
      return null;
    }
  });

  /** Si el usuario estaba en una mesa como invitado, el back lo vincula por el token del comensal. */
  login(credentials: LoginRequest): Observable<AuthResponse> {
    return this.authApi.login(credentials).pipe(
      tap((response: AuthResponse) => {
        const processed = TokenManager.processResponse(response);
        this.authState.applyAuthData(processed);
      }),
      catchError((error) => {
        logger.warn('Autenticación', 'Error en login', error);
        return throwError(() => error);
      }),
    );
  }

  register(data: unknown): Observable<UserDetailResponse> {
    return this.authApi.register(data).pipe(
      catchError((error) => {
        logger.warn('Autenticación', 'Error en registro', error);
        return throwError(() => error);
      }),
    );
  }

  resendVerificationEmail(email: string): Observable<void> {
    return this.authApi.resendVerificationEmail(email).pipe(
      catchError((error) => {
        logger.warn('Autenticación', 'Error reenviando email', error);
        return throwError(() => error);
      }),
    );
  }

  forgotPassword(data: { email: string }): Observable<void> {
    return this.authApi.requestPasswordReset(data).pipe(
      catchError((error) => {
        logger.warn('Autenticación', 'Error en solicitud de reseteo', error);
        return throwError(() => error);
      }),
    );
  }

  resetPassword(recoveryToken: string, newPassword: string): Observable<unknown> {
    const data = { recoveryToken, newPassword };

    return this.authApi.performPasswordReset(data).pipe(
      catchError((error) => {
        logger.warn('Autenticación', 'Error en reseteo de contraseña', error);
        return throwError(() => error);
      }),
    );
  }

  verifyEmail(token: string): Observable<void> {
    return this.authApi.verifyEmail(token).pipe(
      catchError((error) => {
        logger.warn('Autenticación', 'Error verificando email', error);
        return throwError(() => error);
      }),
    );
  }

  scanQR(request: TableAccessRequest, forceChange = false): Observable<AuthResponse> {
    const currentSessionId = this.authState.tableSessionId();
    const hasValidSession = SessionUtils.isValidSession(currentSessionId);

    if (hasValidSession && !forceChange) {
      return throwError(() => ({
        status: 409,
        error: { message: 'Ya tienes una sesión activa' },
      }));
    }

    if (hasValidSession && forceChange) {
      return this.closeCurrentSession().pipe(switchMap(() => this.performScanQR(request)));
    }

    return this.performScanQR(request);
  }

  checkAuthStatus(): Observable<boolean> {
    const token = SessionUtils.getCleanStorageValue('accessToken');

    if (!token || !JwtUtils.isValidToken(token)) {
      this.performLocalLogout();
      return of(false);
    }

    this.authState.loadFromStorage();
    return of(true);
  }

  logout(): Observable<void> {
    const refreshToken = this.authState.refreshToken();

    return this.authApi.logout(refreshToken).pipe(
      tap(() => {
        this.performLocalLogout();
      }),
      catchError((error) => {
        logger.error(
          'Autenticación',
          'Error en logout del backend, realizando logout local',
          error,
        );

        this.performLocalLogout();
        return of(void 0);
      }),
    );
  }

  /**
   * Procesa un AuthResponse crudo del backend (login, cambio de contexto/rol)
   * con TokenManager y lo aplica al estado. Usar esto cuando se recibe la
   * respuesta directa de la API (p. ej. RootApiService.selectContext), de modo
   * que foodVenueId/tableSessionId/participantCount se deriven correctamente.
   */
  applyAuthResponse(response: AuthResponse) {
    this.authState.applyAuthData(TokenManager.processResponse(response));
  }

  private performLocalLogout(): void {
    this.authState.clearState();
    this.cartService.clear();
    this.menuService.clearCache();
    this.foodVenueService.clearAll();

    SessionUtils.clearAllAuthData();
  }

  getSessionInfoFromResponse(response: AuthResponse): {
    tableNumber?: number;
    activeParticipants?: unknown[];
    previousParticipants?: unknown[];
  } | null {
    return TokenManager.getSessionInfoFromResponse(response);
  }

  getAvailableRoles(): Observable<Employment[]> {
    return this.authApi.getAvailableRoles().pipe(
      catchError((error) => {
        logger.error('Autenticación', 'Error obteniendo roles', error);
        return throwError(() => error);
      }),
    );
  }

  selectRole(employmentId: string): Observable<AuthResponse> {
    const currentEmployments = this.authState.employments();
    return this.authApi.selectRole(employmentId).pipe(
      tap((response) => {
        // La API devuelve un nuevo token, así que lo procesamos y actualizamos todo el estado.
        const processed = TokenManager.processResponse(response as AuthResponse);
        processed.employments = currentEmployments;
        this.authState.applyAuthData(processed);
      }),
      catchError((error) => {
        logger.error('Autenticación', 'Error al seleccionar el rol', error);
        return throwError(() => error);
      }),
    );
  }

  getAvailableEmployments(response: AuthResponse): Employment[] {
    return TokenManager.getEmploymentsFromResponse(response);
  }

  // ==================== MÉTODOS PRIVADOS ====================

  private performScanQR(request: TableAccessRequest): Observable<AuthResponse> {
    return this.authApi.scanQR(request).pipe(
      tap((response: AuthResponse) => {
        const processed = TokenManager.processResponse(response);
        this.authState.applyAuthData(processed);
      }),
      catchError((error) => {
        logger.error('Autenticación', 'Error escaneando QR', error);
        return throwError(() => error);
      }),
    );
  }

  private closeCurrentSession(): Observable<void> {
    this.authState.clearSessionData();
    SessionUtils.clearAllSessionData();
    return of(void 0);
  }

  /**
   * Renueva el access token. Si ya hay una renovación en curso, devuelve esa misma: todas las
   * peticiones que la esperan reciben el token nuevo, o el error si falla (antes, si fallaba, las
   * que esperaban quedaban colgadas).
   */
  refreshAccessToken(): Observable<string> {
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    const currentRefreshToken = this.authState.refreshToken();

    if (!currentRefreshToken || currentRefreshToken === 'guest') {
      this.logoutAndReload();
      return throwError(() => new Error('No hay refresh token válido.'));
    }

    this.refreshInFlight$ = this.authApi.refreshToken(currentRefreshToken).pipe(
      tap((response: AuthResponse) => {
        const processed = TokenManager.processResponse(response);
        this.authState.applyAuthData(processed);
      }),
      map((response) => response.accessToken),
      catchError((error) => {
        this.logoutAndReload();
        return throwError(() => error);
      }),
      finalize(() => (this.refreshInFlight$ = null)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    return this.refreshInFlight$;
  }

  logoutAndReload(): void {
    this.logout().subscribe({
      complete: () => location.reload(),
      error: () => location.reload(),
    });
  }

  setPendingTableScan(id: string) {
    this.pendingTableId = id;
  }

  consumePendingTableScan(): string | null {
    const id = this.pendingTableId;
    this.pendingTableId = null;
    return id;
  }

  hasPendingTableScan(): boolean {
    return this.pendingTableId !== null;
  }

  loginWithGoogle(idToken: string): Observable<AuthResponse> {
    return this.authApi.loginWithGoogle(idToken).pipe(
      tap((response) => {
        const processed = TokenManager.processResponse(response as AuthResponse);
        this.authState.applyAuthData(processed);
      }),
      catchError((error) => {
        logger.warn('Autenticación', 'Error en Google login', error);
        return throwError(() => error);
      }),
    );
  }
}
